import React from 'react';
import classnames from 'classnames';
import { NotificationRenderFunction, NotificationType } from '../../../Notifications/redux/types';
import CustomNotification from '../../../Notifications/CustomNotification';
import PopperPositionedPopover from '@wix/da-ds/pkg/popper/PopperPositionedPopover';
import DefaultPopoverContent from '@wix/da-ds/pkg/popper/_partials/DefaultPopoverContent';
import type { OffsetModifier, ReferenceChildrenProps } from '@wix/da-ds/pkg/popper/types';
import popoverStyles from './PopoverWithTooltip.scss';

export interface Props {
  arrowClassName?: string;
  contentClassName?: string;
  referenceClassName?: string;
  children?: React.ReactNode;
  tooltipOffset?: OffsetModifier;
  notificationTypes?: (string | NotificationType)[];
  customNotificationRender?: NotificationRenderFunction;
  onReferenceClick?: (e: React.MouseEvent) => void;
  /**
   * ONLY USE THIS IF YOU KNOW WHAT YOU ARE DOING
   * By default, children passed to this component will be the reference element for the popper,
   * or alternatively you can pass a renderReference prop. This is helpful if you need to make the
   * reference element be some inner node on the entire structure of the tooltip.
   */
  renderReference?: (props: ReferenceChildrenProps) => React.ReactNode;
}

const NotificationPopover: React.FC<Props> = ({
  referenceClassName,
  onReferenceClick,
  children,
  tooltipOffset = [0, 8],
  contentClassName,
  arrowClassName,
  notificationTypes,
  customNotificationRender,
  renderReference,
}) => {
  return (
    <PopperPositionedPopover
      renderPopover={({
        effectivePlacement,
        effectiveSecondaryPlacement,
        rootRef,
        arrowRef,
        contentStyle,
        arrowStyle,
        updatePositioning,
        arrowAttributes,
        contentAttributes,
      }) => (
        <DefaultPopoverContent
          effectivePlacement={effectivePlacement}
          effectiveSecondaryPlacement={effectiveSecondaryPlacement}
          rootRef={rootRef}
          arrowRef={arrowRef}
          contentStyle={contentStyle}
          arrowStyle={arrowStyle}
          contentAttributes={contentAttributes}
          arrowAttributes={arrowAttributes}
          updatePositioning={updatePositioning}
          showArrow
          wrapperClassName={popoverStyles['notification-wrapper']}
          contentClassName={classnames(contentClassName, popoverStyles['notification'])}
          arrowClassName={classnames(arrowClassName, popoverStyles['notification-arrow'])}
        >
          <CustomNotification
            notificationTypes={notificationTypes}
            customNotificationRender={customNotificationRender}
          />
        </DefaultPopoverContent>
      )}
      placement="top"
      secondaryPlacement="start"
      usePortal
      className={referenceClassName}
      onReferenceClick={onReferenceClick}
      offsetModifier={tooltipOffset}
      renderReference={renderReference}
    >
      {children}
    </PopperPositionedPopover>
  );
};
NotificationPopover.displayName = 'NotificationPopover';

export default NotificationPopover;
